<template>
    <div>
        <div class="kgr-row kgr-row--smallMargin">
            <kgr-appointment-series-table
                :appointment-series-list="appointmentSeriesList"
                @remove_interval="removeAppointmentSeries"
                @edit_interval="editAppointmentSeries"
            />
            <kgr-form-row
                :error="error"
                field-name="appointmentSeriesList"
            />
        </div>
        <div v-show="showForm">
            <div class="kgr-inputGroup">
                <div class="kgr-grid kgr-grid--gutters">
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Startdatum')"
                            :error="error"
                            field-name="startDate"
                            required
                        >
                            <kgr-date-picker
                                v-model.trim="startDate"
                                @enter="onKeyUpEnter"
                                @post-input="setAppointmentSeriesEndDate"
                            />
                        </kgr-form-row>
                    </div>
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Start')"
                            :error="error"
                            field-name="startTime"
                            required
                        >
                            <input
                                v-model.trim="startTime"
                                type="time"
                                class="kgr-input"
                                @enter="onKeyUpEnter"
                                @input="checkForm()"
                            />
                        </kgr-form-row>
                    </div>
                </div>
            </div>
            <div class="kgr-inputGroup">
                <div class="kgr-grid kgr-grid--gutters">
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Enddatum')"
                            :error="error"
                            field-name="endDate"
                            required
                        >
                            <kgr-date-picker
                                v-model.trim="endDate"
                                @enter="onKeyUpEnter"
                                @post-input="checkForm()"
                            />
                        </kgr-form-row>
                    </div>
                    <div class="kgr-grid__cell">
                        <kgr-form-row
                            :label="$t('Ende')"
                            :error="error"
                            field-name="endTime"
                            required
                        >
                            <input
                                v-model.trim="endTime"
                                type="time"
                                class="kgr-input"
                                @enter="onKeyUpEnter"
                                @input="checkForm()"
                            />
                        </kgr-form-row>
                    </div>
                </div>
            </div>
            <kgr-form-row
                :label="$t('offering.interval')"
                :error="error"
                field-name="interval"
                required
            >
                <kgr-generic-select
                    v-model="interval"
                    :options="intervals"
                    display="name"
                />
            </kgr-form-row>

            <kgr-form-row
                v-show="interval && interval.value !== null"
                :label="$t('offering.intervalEndDate')"
                :error="error"
                field-name="intervalEndDate"
            >
                <kgr-date-picker
                    v-model.trim="intervalEndDate"
                    :min-date="minIntervalEndDate"
                    @enter="onKeyUpEnter"
                    @post-input="checkForm()"
                />
            </kgr-form-row>
            <br />
            <button
                class="kgr-button"
                @click="clearForm"
            >
                {{
                    $t('offering.cancel')
                }}
            </button>
            <kgr-button
                v-if="!inEditMode"
                :value="$t('offering.btnAddInterval')"
                :disabled="disableSave"
                @click="addAppointmentSeries"
            />
            <kgr-button
                v-else
                :value="$t('offering.btnEditInterval')"
                :disabled="disableSave"
                @click="addAppointmentSeries"
            />
        </div>
        <div v-show="!showForm">
            <button
                class="kgr-button kgr-button--accent"
                @click="toggleShowForm"
            >
                {{
                    $t('offering.btnAddInterval')
                }}
            </button>
        </div>
    </div>
</template>

<script>
import DatePicker from '../common/form/picker/DatePicker.vue';
import Error from '../../js/model/Error';
import FormRow from '../common/form/FormRow.vue';
import GenericSelect from '../common/form/select/GenericSelect.vue';
import Interval from '../../js/offering/Interval';
import KgrAppointmentSeriesTable from './KgrAppointmentSeriesTable.vue';
import KgrButton from '../common/form/button/KgrButton.vue';
import Offering from '../../js/dto/offering/Offering';
import moment from 'moment/moment';

export default {
    name: 'KgrOfferingAppointmentSeriesComponent',
    components: {
        'kgr-button': KgrButton,
        'kgr-form-row': FormRow,
        'kgr-date-picker': DatePicker,
        'kgr-generic-select': GenericSelect,
        'kgr-appointment-series-table': KgrAppointmentSeriesTable,
    },
    props: {
        value: {
            type: Array,
            required: true,
        },
        offering: {
            type: Offering,
            required: true,
        },
        saveAction: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            appointmentSeriesList: null,
            error: new Error(),
            disableSave: false,
            intervals: [
                { name: this.$t('interval.weekly'), value: 'weekly' },
                { name: this.$t('interval.biweekly'), value: 'biweekly' },
                { name: this.$t('interval.mondayToFriday'), value: 'mondayToFriday' },
                { name: this.$t('interval.mondayToSaturday'), value: 'mondayToSaturday' },
                { name: this.$t('interval.daily'), value: 'daily' },
                { name: this.$t('interval.once'), value: null },
            ],
            weekdays: [
                { name: this.$t('weekday_1'), value: 1 },
                { name: this.$t('weekday_2'), value: 2 },
                { name: this.$t('weekday_3'), value: 3 },
                { name: this.$t('weekday_4'), value: 4 },
                { name: this.$t('weekday_5'), value: 5 },
                { name: this.$t('weekday_6'), value: 6 },
                { name: this.$t('weekday_7'), value: 7 },
            ],
            showForm: false,
            inEditMode: false,
            interval: null,
            offeringIntervalId: null,
            intervalEndDate: '',
            startDate: '',
            endDate: '',
            startTime: '',
            endTime: '',
        };
    },
    computed: {
        getWeekday(weekdayValue) {
            return this.weekdays.find((weekday) => weekday.value === weekdayValue);
        },
        getInterval(intervalValue) {
            if (intervalValue) {
                return this.intervals.find((interval) => interval.value === intervalValue.type);
            }
            return null;
        },
        isEmptyForm() {
            return !this.showForm
                || (this.startDate === '' || this.endDate === null)
                && (this.startTime === '' || this.startTime === null)
                && (this.endDate === '' || this.endDate === null)
                && (this.endTime === '' || this.endDate === null)
                && (this.interval === '' || this.interval === null)
                && (this.intervalEndDate === '' || this.intervalEndDate === null);
        },
        minIntervalEndDate() {
            if (!this.startDate) {
                return null;
            }
            return moment(this.startDate, 'DD.MM.YYYY').add(1, 'days').toDate();
        },
    },
    watch: {
        saveAction() {
            if (this.saveAction && this.showForm) {
                this.addAppointmentSeries();
            }
        },
        interval(newInterval) {
            if (!newInterval || newInterval.value === null) {
                this.intervalEndDate = null;
            } else if (!this.intervalEndDate && this.startDate) {
                this.intervalEndDate = moment(this.startDate, 'DD.MM.YYYY').add(1, 'days').format('DD.MM.YYYY');
            }
        },
    },
    created() {
        this.appointmentSeriesList = this.value;

        const params = new URLSearchParams(window.location.search.substring(1));
        if (params.has('start')) {
            const start = moment(new Date(params.get('start')));
            if (start.isValid()) {
                this.startDate = start.format('DD.MM.YYYY');
                this.startTime = start.format('HH:mm');
                this.endDate = start.format('DD.MM.YYYY');
                this.showForm = true;
            }
        }
        if (params.has('end')) {
            const end = moment(new Date(params.get('end')));
            if (end.isValid()) {
                this.endDate = end.format('DD.MM.YYYY');
                this.endTime = end.format('HH:mm');
                this.showForm = true;
            }
        }
    },
    methods: {
        onKeyUpEnter() {
            this.$emit('enter');
        },
        toggleShowForm() {
            this.showForm = !this.showForm;
        },
        removeAppointmentSeries(intervalData) {
            this.appointmentSeriesList = this.appointmentSeriesList.filter((interval) => interval !== intervalData);

            this.$emit('change', this.appointmentSeriesList);
        },
        setAppointmentSeriesEndDate() {
            if (!this.endDate) {
                this.endDate = this.startDate;
            }
            this.checkForm();
        },
        clearForm() {
            this.error = new Error();
            this.disableSave = false;
            this.$emit('appointmentInvalid', false);
            this.interval = null;
            this.startDate = null;
            this.startTime = null;
            this.endDate = null;
            this.endTime = null;
            this.offeringWeekdays = null;
            this.showInterval = false;
            this.offeringIntervalId = null;
            this.showForm = false;
            this.inEditMode = false;
        },
        editAppointmentSeries(interval) {
            this.showForm = true;
            this.inEditMode = true;
            this.offeringIntervalId = interval.id;
            this.interval = this.getInterval(interval.interval);
            this.offeringWeekdays = this.getWeekday(interval.weekday);
            this.startTime = moment(interval.start, 'YYYY-MM-DD HH:mm').format('HH:mm');
            this.endTime = moment(interval.end, 'YYYY-MM-DD HH:mm').format('HH:mm');
            this.startDate = moment(interval.start, 'YYYY-MM-DD HH:mm').format('DD.MM.YYYY');
            this.endDate = moment(interval.end, 'YYYY-MM-DD HH:mm').format('DD.MM.YYYY');
            if (interval.intervalEnd) {
                this.intervalEndDate = moment(interval.intervalEnd, 'YYYY-MM-DD HH:mm').format('DD.MM.YYYY');
            }
        },
        addAppointmentSeries() {
            this.checkForm();
            if (this.error.details.length !== 0 || this.isEmptyForm) {
                return;
            }

            this.appointmentSeriesList.push({
                interval: this.interval && this.interval.value
                    ? {
                            type: this.interval.value,
                            weekday: moment(`${this.startDate} ${this.startTime}`, 'DD.MM.YYYY HH:mm').isoWeekday(),
                            date: moment(`${this.startDate} ${this.startTime}`, 'DD.MM.YYYY HH:mm').format('YYYY-MM-DD'),
                            day: Number.parseInt(moment(`${this.startDate} ${this.startTime}`, 'DD.MM.YYYY HH:mm').format('DD')),
                        }
                    : null,
                weekday: moment(`${this.startDate} ${this.startTime}`, 'DD.MM.YYYY HH:mm').isoWeekday(),
                start: moment(`${this.startDate} ${this.startTime}`, 'DD.MM.YYYY HH:mm').format('YYYY-MM-DD HH:mm'),
                end: moment(`${this.endDate} ${this.endTime}`, 'DD.MM.YYYY HH:mm').format('YYYY-MM-DD HH:mm'),
                intervalEnd: this.intervalEndDate ? moment(this.intervalEndDate, 'DD.MM.YYYY').format('YYYY-MM-DD') : null,
            });
            this.interval = null;
            this.startDate = null;
            this.startTime = null;
            this.endDate = null;
            this.endTime = null;
            this.offeringWeekdays = null;
            this.showInterval = false;
            this.offeringIntervalId = null;
            this.showForm = false;

            this.$emit('change', this.appointmentSeriesList);
        },
        checkForm() {
            this.error = new Error();
            this.error.details = [];
            if (!this.startDate) {
                this.error.details.push({
                    attributeName: 'startDate',
                    constraintMessage: this.$t('offering.error.chooseStartDate'),
                });
            }
            if (!this.endDate) {
                this.error.details.push({
                    attributeName: 'endDate',
                    constraintMessage: this.$t('offering.error.chooseEndDate'),
                });
            }
            if (!this.startTime) {
                this.error.details.push({
                    attributeName: 'startTime',
                    constraintMessage: this.$t('offering.error.chooseStartTime'),
                });
            }
            if (!this.endTime) {
                this.error.details.push({
                    attributeName: 'endTime',
                    constraintMessage: this.$t('offering.error.chooseEndTime'),
                });
            }

            if (moment(`${this.endDate} ${this.endTime}`, 'DD.MM.YYYY HH:mm').diff(moment(`${this.startDate} ${this.startTime}`, 'DD.MM.YYYY HH:mm'), 'minutes') < 0) {
                this.error.details.push({
                    attributeName: 'startDate',
                    constraintMessage: this.$t('offering.error.EndTimeGreaterStartTime'),
                });
            }

            if (this.startDate
                && this.interval
                && !Interval.coversWeekday(this.interval.value, moment(this.startDate, 'DD.MM.YYYY').isoWeekday())) {
                this.error.details.push({
                    attributeName: 'startDate',
                    constraintMessage: this.$t('offering.error.startDateNotOnIntervalWeekday'),
                });
            }
            if (this.intervalEndDate && this.startDate && moment(this.intervalEndDate, 'DD.MM.YYYY').isSameOrBefore(moment(this.startDate, 'DD.MM.YYYY'), 'day')) {
                this.error.details.push({
                    attributeName: 'intervalEndDate',
                    constraintMessage: this.$t('offering.error.IntervalEndBeforeStartDate'),
                });
            }

            if (this.interval
                && this.interval.value
                && !this.intervalEndDate
                && this.offering.bookingMode === Offering.BOOKING_MODE_OFFERING) {
                this.error.details.push({
                    attributeName: 'intervalEndDate',
                    constraintMessage: this.$t('offering.error.noIntervalEndDateAndBookingModeOffering'),
                });
            }
            if (this.error.details.length !== 0) {
                this.disableSave = true;
            } else {
                this.disableSave = false;
            }
            this.$emit('appointmentInvalid', this.disableSave);
        },
    },
};
</script>

<style scoped lang="scss" rel="stylesheet/scss"></style>
